<template>
  <div>
    <va-modal
      :value="showModal"
      id="availabilityFormModal"
      :size="form.facility && hasAppointments ? 'large' : 'medium'"
      :noOutsideDismiss="true"
      :okText="'users.save' | translate"
      :cancelText="'users.cancel' | translate"
      :dangerText="'references.inputs.delete' | translate"
      :showDelete="selectedAvailability != undefined"
      @ok="submit"
      :okDisabled="isSubmitting || !canSubmitForm"
      @cancel="cancel"
      @remove="remove">

      <div slot="header" class="modal-title">
        {{ getFormTitle }}
        <span v-if="this.form.facility && this.form.facility.entity && (this.form.facility.entity.status === 1 || this.form.facility.entity.status === 2)" class="facility-status-icon">
          <va-popover :message="$t(`entities.full_status.${this.form.facility.entity.status}`)" placement="right">
              <span v-if="this.form.facility.entity.status === 1" class="i-evollu-status-pending"></span>
              <span v-if="this.form.facility.entity.status === 2" class="i-evollu-status-inactive"></span>
          </va-popover>
        </span>
      </div>

      <div class="row">
        <div :class="{'col-12 col-md-6': hasAppointments, 'col-12': !hasAppointments}">

          <div class="row" v-if="isUpdating && facilities.length">
            <div class="col">
              <div>
                <label class="form-label">{{$t('agenda.appointments.facility')}}:</label>
                <div class="form-group" style="margin-bottom:1.25rem;">
                  <div class="input-group">
                    <multiselect
                      v-model="form.facility"
                      :options="facilities"
                      track-by="id"
                      label="name"
                      :custom-label="nameWithCity"
                      placeholder="---"
                      :allow-empty="true"
                      :multiple="false"
                      :searchable="true"
                      :disabled="true"
                      >
                      <span slot="noResult">{{'patients.noResults'| translate}}</span>
                    </multiselect>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row" v-if="!isUpdating && facilities.length">
            <div class="col">
              <div>
                <label class="form-label">{{$t('agenda.appointments.facility')}}:</label>
                <div class="form-group" style="margin-bottom:1.25rem;">
                  <div class="input-group">
                    <multiselect
                      v-model="form.facility"
                      :options="activeFacilities"
                      track-by="id"
                      label="name"
                      :custom-label="nameWithCity"
                      placeholder="---"
                      :allow-empty="true"
                      :multiple="false"
                      :searchable="true"
                      @input="validationErrors.clear('facility_id')"
                      >
                      <span slot="noResult">{{'patients.noResults'| translate}}</span>
                    </multiselect>
                    <small v-show="validationErrors.has('facility_id')" class="help text-danger">
                      {{ validationErrors.get('facility_id') | translate }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ExpandTransition>
            <div v-if="form.facility">

              <!-- Aud Provider Multiselect -->
              <div class="row" v-if="canEditAudiologyProvider">
                <div class="col">
                  <div>
                    <label class="form-label">{{$t('agenda.appointments.aud_provider')}}:</label>
                    <div class="form-group aud-provider-multiselect-wrapper" style="margin-bottom:1.25rem;">
                      <div class="input-group">
                        <multiselect
                          v-model="form.aud_provider"
                          :options="activeAvailableAudProviders"
                          track-by="id"
                          label="name"
                          :custom-label="nameWithCity"
                          placeholder="---"
                          :allow-empty="true"
                          :multiple="false"
                          :searchable="true"
                          @input="validationErrors.clear('aud_provider_id')">
                          <span slot="noResult">{{ $t('patients.noResults') }}</span>
                          <template slot="singleLabel" slot-scope="props">
                            <span>{{ props.option.name }}</span>
                            <span v-if="props.option.status === 1 || props.option.status === 2" class="status-warning">
                              <span v-if="props.option.status === 1" class="pending">{{ $t('entities.status.warning') }}&nbsp;{{ $t(`entities.full_status.${props.option.status}`) }}&nbsp;&nbsp;<span class="i-evollu-status-pending"></span></span>
                              <span v-if="props.option.status === 2">{{ $t('entities.status.warning') }}&nbsp;{{ $t(`entities.full_status.${props.option.status}`) }}&nbsp;&nbsp;<span class="i-evollu-status-inactive"></span></span>
                            </span>
                          </template>
                          <template slot="option" slot-scope="props">
                            <span>{{ props.option.name }}</span>
                            <span v-if="props.option.status === 1 || props.option.status === 2" class="status-warning">
                              <span v-if="props.option.status === 1" class="pending">{{ $t('entities.status.warning') }}&nbsp;{{ $t(`entities.full_status.${props.option.status}`) }}&nbsp;&nbsp;<span class="i-evollu-status-pending"></span></span>
                              <span v-if="props.option.status === 2">{{ $t('entities.status.warning') }}&nbsp;{{ $t(`entities.full_status.${props.option.status}`) }}&nbsp;&nbsp;<span class="i-evollu-status-inactive"></span></span>
                            </span>
                          </template>
                        </multiselect>
                        <small v-show="validationErrors.has('aud_provider_id')" class="help text-danger">
                          {{ validationErrors.get('aud_provider_id') | translate }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Start and End Form Inputs -->
              <div class="row mt-2">
                <div class="col-6">
                  <div class="form-group form-group-agenda" :class="{'has-error': validationErrors.has('start')}">
                    <div class="input-group">
                      <va-date-picker
                        class="w-100"
                        v-model="form.start"
                        @input="onStartInputChanged"
                        :label="$t('agenda.availability.start')"
                        :config="{
                          enableTime: true,
                          time_24hr: true,
                          minuteIncrement: 15,
                          dateFormat: 'Y-m-d H:i:S',
                          defaultHour: '09'
                        }"/>
                      <i class="bar"></i>
                      <small v-show="validationErrors.has('start')" class="help text-danger">
                        {{ validationErrors.get('start') | translate }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="col-6">
                  <div class="form-group form-group-agenda" :class="{'has-error': validationErrors.has('end')}">
                    <div class="input-group">
                      <va-date-picker
                        class="w-100"
                        v-model="form.end"
                        :label="$t('agenda.availability.end')"
                        :config="{
                          enableTime: true,
                          time_24hr: true,
                          minuteIncrement: 15,
                          dateFormat: 'Y-m-d H:i:S',
                          defaultHour: '18'
                        }"/>
                      <i class="bar"></i>
                      <small v-show="validationErrors.has('end')" class="help text-danger">
                        {{ validationErrors.get('end') | translate }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Select availability activity dropdown -->
              <div class="row">
                <div class="col">
                  <label class="form-label">{{$t('agenda.inputs.service')}}:</label>
                  <div class="form-group" style="margin-bottom:1.25rem;">
                    <fade-transition
                      :duration="200"
                      mode="out-in">
                        <div v-if="Array.isArray(activitiesForFacility)" class="form-group form-group-agenda" style="margin-bottom: 1rem;">
                          <div class="input-group">
                            <multiselect
                              v-model="form.activity"
                              :options="activitiesForFacility"
                              label="name"
                              track-by="id"
                              :disabled="activitiesForFacility.length === 1"
                              placeholder="---"
                              :allow-empty="true"
                              :multiple="false"
                              :searchable="true" >
                              <span slot="noResult">{{'patients.noResults'| translate}}</span>
                              <template slot="singleLabel" slot-scope="{ option }">
                                <span v-if="option && option.id">{{ $t(`activities.${option.id}`, option.name) }}</span>
                              </template>
                              <template slot="option" slot-scope="{option}">
                                <span v-if="option && option.id">{{ $t(`activities.${option.id}`, option.name) }}</span>
                              </template>
                            </multiselect>
                          </div>
                        </div>
                        <div v-else class="d-flex justify-content-start pl-4 align-items-center">
                          <loading-animation :size="'20px'"></loading-animation>
                        </div>
                    </fade-transition>
                  </div>
                </div>
              </div>

              <!-- Available Online Field -->
              <div class="row">
                <div class="col-6">
                  <div style="margin-bottom: 1rem;">
                    <small class="help text-primary my-label">{{$t('agenda.availability.public_label')}}
                      <va-popover
                        :message="$t('agenda.availability.public_tooltip')"
                        placement="top"
                      >
                      <i class="fa fa-info-circle"></i>
                      </va-popover>
                    </small>
                    <br>
                    <span class="select is-fullwidth is-small">
                      <va-checkbox
                      :label="$t('agenda.availability.public')"
                      id="checkbox-public"
                      :isCircle="true"
                      v-model="form.public"/>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Appointment cluster -->
               <div class="row">
                <div class="col-12">
                  <label class="form-label">{{$t('agenda.inputs.appointments_cluster')}}:</label>
                  <div class="form-group user-multiselect-wrapper" :class="{'has-error': validationErrors.has('cluster_id')}" style="margin-bottom: 1rem;">
                    <div class="input-group">
                      <multiselect
                        v-model="form.cluster"
                        :options="appointmentClusters"
                        track-by="id"
                        :placeholder="$t('agenda.labels.clusters')"
                        :custom-label="formatedEntityLabel"
                        :allow-empty="true"
                        :disabled="false"
                        :multiple="false"
                        :searchable="true"
                        @input="validationErrors.clear('cluster_id')">
                        <span slot="noResult">{{$t('patients.noResults')}}</span>
                        <span slot="noOptions">{{$t('patients.noResults')}}</span>
                      </multiselect>
                      <small v-show="validationErrors.has('cluster_id')" class="help text-danger">
                        {{ validationErrors.get('cluster_id') | translate }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Assign Staff Dropdown -->
              <div class="row">
                <div class="col-12">
                  <label class="form-label">{{$t('agenda.inputs.professionals')}}:</label>
                  <div class="form-group user-multiselect-wrapper" :class="{'has-error': validationErrors.has('user_id')}" style="margin-bottom: 1rem;">
                    <div class="input-group">
                      <multiselect
                        v-model="form.user"
                        :options="availableUsersForFacilityAndActivity"
                        label="name"
                        track-by="id"
                        placeholder="---"
                        :allow-empty="true"
                        :disabled="false"
                        :multiple="false"
                        :searchable="true"
                        @input="validationErrors.clear('user_id')">
                        <template slot="singleLabel" slot-scope="props">
                          <span>{{ props.option.name }}</span>
                          <span v-if="props.option.id && props.option.entity.status === 2" class="status-warning">
                            <b>{{ $t('entities.status.warning') }}</b>&nbsp;{{ $t('entities.status.warning_user_desc') }}&nbsp;&nbsp;<span class="i-evollu-warning"></span>
                          </span>
                        </template>
                        <template slot="option" slot-scope="props">
                          <span>{{ `${props.option.name} (${props.option.entity ? props.option.entity.name : 'N/A' })`}}</span>
                        </template>
                        <span slot="noResult">{{$t('patients.noResults')}}</span>
                        <span slot="noOptions">{{$t('patients.noResults')}}</span>
                      </multiselect>
                      <small v-show="validationErrors.has('user_id')" class="help text-danger">
                        {{ validationErrors.get('user_id') | translate }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Observations Input -->
              <div class="row">
                <div class="col-12">
                  <va-input
                    v-model="form.observations"
                    :label="$t('agenda.inputs.observations')"
                    type="textarea"
                    :minRows="3"
                    :maxRows="8" />
                </div>
              </div>
              <!-- <div class="form-group mt-3">
                <div class="input-group">
                  <input id="simple-input" placeholder="" type="text" v-model="form.observations" maxlength="250"/>
                  <label class="control-label" for="simple-input"> {{$t('agenda.inputs.observations')}}</label><i class="bar"></i>
                </div>
              </div> -->
            </div>
          </ExpandTransition>
        </div>

        <AppointmentsList
          class="col-12 col-md-6"
          v-if="hasAppointments"
          :screening="selectedAvailability"
          :appointments="appointments"
          />
      </div>
    </va-modal>

    <AvailabilityModalDelete
      v-if="showDeletingModal"
      :availability="selectedAvailability"
      :appointments="appointments"
      @actionDeletedConfirmed="availabilityDeleted"
      @actionDeleteCanceled="availabilityDeleteCanceled" />
  </div>
</template>
<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import AppointmentsList from '../appointments/AppointmentsList'
import Errors from '@/utils/StandardErrors.js'
import AvailabilityModalDelete from './AvailabilityDelete'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import ExpandTransition from '@/components/common/transitions/ExpandTransition'
// import LoadingAnimation from '@/components/common/LoadingAnimationCircle'
import { AvailabilityService, FacilityService } from '@/api/BookingService'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'

const errorsPrefix = 'agenda.availability.validationMessages.'

export default {
  components: {
    Multiselect,
    FadeTransition,
    AppointmentsList,
    // FadeTransition,
    ExpandTransition,
    // LoadingAnimation,
    AvailabilityModalDelete
  },
  props: {
    fromEvent: {
      type: Object,
      required: false
    },
    facilities: {
      type: Array,
      required: true
    },
    availableActivities: {
      type: Array,
      required: true
    },
    selectedAvailability: {
      type: Object,
      required: false
    }
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  data () {
    return {
      isSubmitting: false,
      canClose: false,
      canCancel: true,
      show: true,
      form: {
        facility: null,
        aud_provider: null,
        start: null,
        end: null,
        user: null,
        activity: null,
        public: true,
        cluster: null,
        recurrence: null,
        observations: null
      },
      showRecurrence: false,
      appointments: [],
      availableWorkActivities: [],
      validationErrors: new Errors(),
      showDeletingModal: false,
      showModal: false
    }
  },
  created () {
    if (this.isUpdating) {
      this.loadAppointments()
      this.fillForm()
    } else {
      this.preFillForm()
    }
  },
  mounted () {
    this.showModal = true
  },
  methods: {
    cancel: function () {
      this.showModal = false
      setTimeout(() => {
        this.$emit('modal-closed')
      }, 250)
    },
    onStartInputChanged: function (start) {
      let startDate = start.substring(0, 10)
      let endTime = this.form.end.substring(11, 19)
      this.form.end = moment(`${startDate} ${endTime}`).format('YYYY-MM-DD HH:mm:ss')
    },
    loadAppointments: function () {
      AvailabilityService.getAppointments(this.selectedAvailability.id)
        .then(appointments => {
          this.appointments = appointments.map(appointment => {
            return {
              ...appointment,
              start: this.$options.filters.localTime(appointment.start),
              end: this.$options.filters.localTime(appointment.end)
            }
          })
        }).catch(err => {
          console.log('err', err)
        })
    },
    campaignAdded: function ({ appointments }) {
      this.appointments = this.appointments.concat(appointments)
      this.appointments.sort((a, b) => {
        return (new Date(a.start)) - (new Date(b.start))
      })
    },
    fillForm: function () {
      Object.assign(this.form, { ...JSON.parse(JSON.stringify(this.selectedAvailability)) })
      this.form.facility = this.facilities.find(facility => facility.id === this.selectedAvailability.facility.id)

      this.form.cluster = this.appointmentClusters.find(cluster => cluster.id === this.form.cluster_id)

      this.$set(this.form, 'aud_provider', this.activeAvailableAudProviders.find(audProvider => audProvider.id === this.selectedAvailability.aud_provider_id))
    },
    preFillForm: function () {
      if (this.availableActivities.length === 1) {
        this.form.activity = this.availableActivities[0]
      }
      if (this.activeFacilities.length === 1) {
        this.form.facility = this.activeFacilities[0]
      }
      if (!this.fromEvent) {
        let start = moment().add(1, 'days').set({ hour: 9, minute: 0, seconds: 0, millisecond: 0 })
        let end = moment().add(1, 'days').set({ hour: 18, minute: 0, seconds: 0, millisecond: 0 })
        this.form.start = start.format('YYYY-MM-DD HH:mm:ss')
        this.form.end = end.format('YYYY-MM-DD HH:mm:ss')
      } else {
        this.form.start = this.fromEvent.start
        this.form.end = this.fromEvent.end
      }
    },
    loadWorkActivities () {
      FacilityService.workActivities(this.form.facility.id, { start: this.form.start, end: this.form.end })
        .then((workActivities) => {
          this.availableWorkActivities = workActivities
        }).catch((err) => {
          console.log(err)
        })
    },
    nameWithCity ({ name, address }) {
      if (!address || !address.city) return name
      return name + ' (' + address.city + ')'
    },
    submit: function () {
      let data = this.getFormData()
      this.isUpdating ? this.update(data) : this.create(data)
    },
    create: function (data) {
      console.log('creating')
      this.isSubmitting = true
      AvailabilityService.store(data)
        .then(availability => {
          this.showToast(this.$t('agenda.availability.validationMessages.create_msg'), this.$store.state.successToastOptions)
          this.$emit('availability-created', { availability })
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.validationErrors.record(errors.response.data.errors, errorsPrefix)
          }
          console.log('errors', errors)
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    update: function (data) {
      console.log('updating')
      this.isSubmitting = true
      AvailabilityService.update(this.selectedAvailability.id, data)
        .then(availability => {
          this.showToast(this.$t('agenda.availability.validationMessages.update_msg'), this.$store.state.successToastOptions)
          this.canClose = true
          this.$emit('availability-updated', { availability })
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.validationErrors.record(errors.response.data.errors, errorsPrefix)
          }
          console.log('errors', errors)
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    remove: function () {
      this.showModal = false
      this.showDeletingModal = true
    },
    availabilityDeleted: function (data) {
      console.log('delete here')
      this.isSubmitting = true
      this.$http({
        method: 'DELETE',
        url: this.$store.state.ebooking_server + '/availabilities/' + this.selectedAvailability.id,
        data
      }).then((response) => {
        this.showToast(this.$t('agenda.availability.validationMessages.delete_msg'), this.$store.state.successToastOptions)
        this.$emit('availability-deleted', { availability: this.selectedAvailability })
      }).catch(error => {
        console.log(error)
        this.showToast(this.$t('agenda.availability.validationMessages.delete_error'), this.$store.state.toastOptions)
      }).finally(() => {
        this.isSubmitting = false
      })
    },
    availabilityDeleteCanceled: function () {
      this.validationErrors.clear()
      setTimeout(() => {
        this.showDeletingModal = false
        this.showModal = true
      }, 500)
    },
    getFormData: function () {
      let body = {
        start: this.$options.filters.utcTime(this.form.start),
        end: this.$options.filters.utcTime(this.form.end),
        aud_provider_id: this.form.aud_provider ? this.form.aud_provider.id : null,
        activity_id: this.form.activity ? this.form.activity.id : null,
        user_id: this.form.user ? this.form.user.id : null,
        private: !this.form.public,
        cluster_id: this.form.cluster ? this.form.cluster.id : null,
        observations: this.form.observations
      }

      if (!this.isUpdating) {
        body.facility_id = this.form.facility ? this.form.facility.id : null
      }

      return body
    }
  },
  computed: {
    ...mapGetters([
      'availableAudProviders',
      'clusters'
    ]),
    getFormTitle: function () {
      if (this.form.facility) {
        return this.isUpdating ? `${this.$t('agenda.availability.edit')} - ${this.nameWithCity(this.form.facility)}` : `${this.$t('agenda.availability.create')} - ${this.nameWithCity(this.form.facility)}`
      }
      return this.isUpdating ? `${this.$t('agenda.availability.edit')}` : `${this.$t('agenda.availability.create')}`
    },
    canEditAudiologyProvider: function () {
      return true
    },
    activeFacilities: function () {
      return this.facilities.filter(facility => facility.entity.status !== 2)
    },
    activeAvailableAudProviders: function () {
      if (!this.isUpdating) return this.availableAudProviders.filter(audProvider => audProvider.status !== 2)
      return this.availableAudProviders.filter(audProvider => audProvider.status !== 2 || audProvider.id === this.selectedAvailability.aud_provider_id)
    },
    hasAppointments: function () {
      return this.appointments.length > 0
      // return !!(this.selectedAvailability !== undefined && this.selectedAvailability.appointments && this.selectedAvailability.appointments.length) || this.appointments.length
    },
    isUpdating: function () {
      return this.selectedAvailability !== undefined && this.selectedAvailability !== null
    },
    activitiesForFacility: function () {
      if (!this.availableWorkActivities.length) {
        return []
      }
      let activities = []
      this.availableWorkActivities.forEach(workActivity => {
        if (!activities.some(activity => activity.id === workActivity.service.id) && this.availableActivities.some(activity => activity.id === workActivity.service.id)) {
          activities.push(workActivity.service)
        }
      })
      return activities
    },
    appointmentClusters: function () {
      return this.clusters
    },
    availableUsersForFacilityAndActivity: function () {
      if (!this.availableWorkActivities.length || !this.form.activity || !this.activitiesForFacility.length) {
        return []
      }
      let users = []
      this.availableWorkActivities.forEach(workActivity => {
        if (workActivity.service.id === this.form.activity.id &&
            workActivity.user &&
            ((workActivity.user.entity && workActivity.user.entity.status !== 2) || this.isUpdating && this.selectedAvailability.user && this.selectedAvailability.user.id === workActivity.user.id) &&
            !users.some(user => user.id === workActivity.user.id)) {
          users.push(workActivity.user)
        }
      })
      users.unshift({
        entity: { id: 3, name: 'Evollu Promo', status: 0 },
        id: null,
        name: this.$t('agenda.screenings.labels.undefined_provider')
      })
      return users
    },
    canSubmitForm: function () {
      return this.form.start && this.form.end && this.form.facility && this.form.activity
    }
  },
  watch: {
    'form.facility': {
      handler: function (facility) {
        if (facility) {
          this.form.facility = facility
          this.availableWorkActivities = []
          this.loadWorkActivities()
          if (!this.isUpdating) {
            if (facility.entity && facility.entity.aud_provider) {
              this.form.aud_provider = this.activeAvailableAudProviders.find(provider => provider.id === facility.entity.aud_provider)
            } else {
              this.form.aud_provider = this.activeAvailableAudProviders.find(provider => provider.id === 1)
            }
          }
        }
      },
      immediate: true,
      deep: true
    },
    activitiesForFacility: {
      handler: function (activities) {
        if (!this.isUpdating && activities.length > 0) {
          this.form.activity = activities[0]
        }
      },
      immediate: true
    },
    'form.end': {
      handler: function (end) {
        if (end && this.form.facility) {
          this.loadWorkActivities()
        }
      }
    },
    availableUsersForFacilityAndActivity: function (users) {
      if (this.isUpdating) {
        if (users.length && this.selectedAvailability.user) {
          this.form.user = users.find(user => user.id === this.selectedAvailability.user.id)
        } else if (users.length && this.selectedAvailability.user === null) {
          this.form.user = users[0]
        } else if (this.form.user) {
          this.form.user = null
        }
      } else {
        if (!users.length) {
          return
        }
        let index = users.findIndex(user => user.id === null)
        if (index !== -1) {
          this.form.user = users[index]
        } else {
          this.form.user = users[0]
        }
      }
    }
  }
}
</script>

<style lang="scss">

.facility-status-icon {
  .i-evollu-status-pending,
  .i-evollu-status-inactive {
    width: 18px;
    height: 18px;
    margin-left: 5px;
    vertical-align: middle;
  }
}

.user-multiselect-wrapper,
.aud-provider-multiselect-wrapper {
  .status-warning {
    display: flex;
    float: right;
    font-size: 90%;
    color: #c81414;
    b {
      font-weight: bold;
    }
    .pending {
      color: #f0c814ff;
    }
    .i-evollu-status-pending,
    .i-evollu-status-inactive {
      width: 16px;
      height: 16px;
      vertical-align: middle;
    }
  }
  .multiselect__option--highlight .status-warning {
    color: white;
    .pending {
      color: white;
    }
    .i-evollu-warning {
      background: url('../../../assets/icons/warning-white.svg') no-repeat center center;
    }
    .i-evollu-status-pending {
      background: url('../../../assets/icons/status-pending-white.svg') no-repeat center center;
    }
    .i-evollu-status-inactive {
      background: url('../../../assets/icons/status-inactive-white.svg') no-repeat center center;
    }
  }
}

</style>
